/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : Canvas.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useRef } from "react";
import PropTypes from "prop-types";
import { useDrop } from "react-dnd";
import { Box } from "@mui/material";
import CanvasElement from "./CanvasElement";
import { PAGE_SIZES } from "./pageSize";

export default function Canvas({
  elements,
  selectedElementId,
  onSelectElement,
  onUpdateElement,
  onAddElement,
  pageSettings,
  zoom,
}) {
  const canvasRef = useRef(null);

  let baseWidth = PAGE_SIZES[pageSettings?.pageSize]?.width ?? 794;
  let baseHeight = PAGE_SIZES[pageSettings?.pageSize]?.height ?? 1123;

  if (pageSettings?.pageSize === "Custom") {
    baseWidth = pageSettings?.customWidth ?? 900;
    baseHeight = pageSettings?.customHeight ?? 1200;
  }

  if (pageSettings?.orientation === "Landscape") {
    [baseWidth, baseHeight] = [baseHeight, baseWidth];
  }

  const {
    marginTop = 0,
    marginLeft = 0,
    marginRight = 0,
    marginBottom = 0,
  } = pageSettings || {};
  const zoomFactor = zoom / 100;

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "sidebar-item",
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset();
      if (!canvasRef.current || !offset) return;

      const rect = canvasRef.current.getBoundingClientRect();

      let x = (offset.x - rect.left) / zoomFactor;
      let y = (offset.y - rect.top) / zoomFactor;

      x = Math.max(marginLeft, Math.min(x, baseWidth - marginRight - 100));
      y = Math.max(marginTop, Math.min(y, baseHeight - marginBottom - 40));

      onAddElement(item.type, { x, y });
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <Box
      ref={(node) => {
        canvasRef.current = node;
        if (node) drop(node);
      }}
      sx={{
        flexGrow: 1,
        overflow: "auto",
        bgcolor: "#F4F6F8",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        position: "relative",
      }}
      onClick={() => onSelectElement(null)}
    >
      <Box
        sx={{
          position: "relative",
          width: baseWidth,
          height: baseHeight,
          background: pageSettings?.backgroundColor,

          backgroundImage: `
            linear-gradient(to right, rgba(0,0,0,0.08) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0,0,0,0.08) 1px, transparent 1px)
          `,
          backgroundSize: "20px 20px",

          border: "1px solid #d1d5db",
          boxShadow: "0 0 6px rgba(0,0,0,0.15)",
          transform: `scale(${zoomFactor})`,
          transformOrigin: "top center",
          marginTop: 0,
          userSelect: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderTop: `${marginTop}px solid rgba(0,0,0,0.05)`,
            borderLeft: `${marginLeft}px solid rgba(0,0,0,0.05)`,
            borderRight: `${marginRight}px solid rgba(0,0,0,0.05)`,
            borderBottom: `${marginBottom}px solid rgba(0,0,0,0.05)`,
            pointerEvents: "none",
          }}
        />

        {elements?.map((el) => (
          <CanvasElement
            key={el.id}
            element={el}
            zoom={zoomFactor}
            isSelected={selectedElementId === el.id}
            onSelect={() => onSelectElement(el.id)}
            onUpdate={(id, updates) => {
              const isDivider = el.type === "divider";

              const x = updates.x ?? el.x;
              const y = updates.y ?? el.y;
              const width = updates.width ?? el.width;
              const height = isDivider
                ? el.height
                : (updates.height ?? el.height);

              const mergedEl = {
                ...el,
                ...updates,
                x: Math.min(
                  Math.max(x, marginLeft),
                  baseWidth - marginRight - width,
                ),
                y: Math.min(
                  Math.max(y, marginTop),
                  baseHeight - marginBottom - height,
                ),
                width: Math.min(
                  Math.max(width, 20),
                  baseWidth - marginLeft - marginRight,
                ),
                height: Math.min(
                  Math.max(height, 15),
                  baseHeight - marginTop - marginBottom,
                ),
                properties: {
                  ...el.properties,
                  ...updates.properties,
                },
              };

              onUpdateElement(id, mergedEl);
            }}
          />
        ))}

        {isOver && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
            }}
          />
        )}
      </Box>
    </Box>
  );
}

Canvas.propTypes = {
  elements: PropTypes.arrayOf(PropTypes.any),
  selectedElementId: PropTypes.any,
  onSelectElement: PropTypes.func,
  onUpdateElement: PropTypes.func,
  onAddElement: PropTypes.func,
  zoom: PropTypes.number,
  pageSettings: PropTypes.shape({
    pageSize: PropTypes.string,
    customWidth: PropTypes.number,
    customHeight: PropTypes.number,
    orientation: PropTypes.string,
    backgroundColor: PropTypes.string,
  }),
};
